import * as Sentry from "@sentry/react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { LoginPage } from "./features/Auth/components/LoginPage";
import { RedirectIfLoggedIn } from "./features/Auth/guards/RedirectIfLoggedIn";
import { RedirectIfLoggedOut } from "./features/Auth/guards/RedirectIfLoggedOut";
import { CheckoutRedirect } from "./features/Checkout/pages/CheckoutRedirect";
import { CheckoutSuccessPage } from "./features/Checkout/pages/CheckoutSuccessPage";
import { DataPage } from "./features/Data/pages/DataPage";
import { HomepageSkeleton } from "./features/Home/components/HomepageSkeleton";
import { Homepage } from "./features/Home/pages/Homepage";
import { NotFoundPage } from "./features/Navigation/components/NotFoundPage";
import { PromptbookEditorPageSkeleton } from "./features/PromptbookEditor/components/PromptbookEditorPageSkeleton";
import { PromptbookEditorPage } from "./features/PromptbookEditor/pages/PromptbookEditorPage";
import { RedirectIfMissingAccess } from "./features/Promptbooks/guards/RedirectIfMissingAccess";
import { InviteClaimPage } from "./features/Teams/pages/InviteClaimPage";
import {
  ImportTemplatePage,
  ImportTemplatePageSkeleton,
} from "./features/Templates/pages/ImportTemplatePage/ImportTemplatePage";
import { SemanticModelPage } from "./features/Semantic/pages/SemanticModelPage";
import { SemanticModelProvider } from "./features/Semantic/state/SemanticModelProvider";
import { WelcomePage } from "./features/Welcome/pages/WelcomePage";
import { WorkspacePage } from "./features/Workspaces/pages/WorkspacePage";
import { useSentryTags } from "./services/sentry/useSentryTags";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const AppRoutes = () => {
  useSentryTags();

  return (
    <BrowserRouter>
      <SentryRoutes>
        <Route
          element={
            <RedirectIfLoggedOut to="/login" loadingView={<HomepageSkeleton />}>
              <Homepage />
            </RedirectIfLoggedOut>
          }
        >
          <Route path="/" element={<WelcomePage />} />
          <Route path="/workspace" element={<Navigate to="/" replace />} />
          <Route path="/workspace/:workspaceId" element={<WorkspacePage />} />
          <Route path="/data" element={<DataPage />} />
        </Route>
        <Route path="/promptbook" element={<Navigate to="/" replace />} />
        <Route
          path="/promptbook/:promptbookId"
          element={
            <RedirectIfLoggedOut to="/login" loadingView={<PromptbookEditorPageSkeleton />}>
              <RedirectIfMissingAccess to="/" loadingView={<PromptbookEditorPageSkeleton />}>
                <PromptbookEditorPage />
              </RedirectIfMissingAccess>
            </RedirectIfLoggedOut>
          }
        />
        <Route
          path="/semantic_model/:semanticModelId"
          element={
            <RedirectIfLoggedOut to="/login">
              <SemanticModelProvider>
                <SemanticModelPage />
              </SemanticModelProvider>
            </RedirectIfLoggedOut>
          }
        />
        <Route
          index
          path="/login"
          element={
            <RedirectIfLoggedIn to="/">
              <LoginPage />
            </RedirectIfLoggedIn>
          }
        />
        <Route
          path="/import/template/:templateId"
          element={
            <RedirectIfLoggedOut to="/" loadingView={<ImportTemplatePageSkeleton />}>
              <ImportTemplatePage />
            </RedirectIfLoggedOut>
          }
        />
        <Route
          path="/invite"
          element={
            <RedirectIfLoggedOut to="/login">
              <InviteClaimPage />
            </RedirectIfLoggedOut>
          }
        />
        <Route
          path="/checkout"
          element={
            <RedirectIfLoggedOut to="/login">
              <CheckoutRedirect />
            </RedirectIfLoggedOut>
          }
        />
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </SentryRoutes>
    </BrowserRouter>
  );
};
